import { Users, Briefcase, ShieldCheck, AlertTriangle, DollarSign, Settings, Bell, ChevronRight, X } from "lucide-react";
import { useAdminData } from "../../hooks/useAdminData";
import { notifications as notificationsApi } from "../../api/endpoints";
import { Btn } from "../ui/Btn";
import { Dot } from "../ui/StatusBadge";
import { CORAL, F, T } from "../../theme/tokens";

// ── Notification drawer ───────────────────────────────────────────────────────

export function NotificationDrawer({ open, onClose, onNav }: { open: boolean; onClose: () => void; onNav: (id: string) => void }) {
  const { ADMIN_NOTIFICATIONS: notifications, refetchNotifications } = useAdminData();
  const unreadCount = notifications.filter((n) => !n.read).length;

  function markAllRead() {
    notificationsApi.markAllRead().then(refetchNotifications).catch(() => { });
  }
  function markRead(id: string) {
    notificationsApi.markRead(id).then(refetchNotifications).catch(() => { });
  }

  const notifIcons: Record<string, React.ReactNode> = {
    verification: <ShieldCheck size={14} style={{ color: "#1e40af" }} />,
    report: <AlertTriangle size={14} style={{ color: "#991b1b" }} />,
    payment: <DollarSign size={14} style={{ color: "#166534" }} />,
    system: <Settings size={14} style={{ color: T.sub }} />,
    new_user: <Users size={14} style={{ color: "#7c3aed" }} />,
    posting: <Briefcase size={14} style={{ color: "#92400e" }} />,
  };
  const notifBg: Record<string, string> = {
    verification: "#dbeafe", report: "#fee2e2", payment: "#e4f5ec",
    system: T.s2, new_user: "#ede9fe", posting: "#fef3c7",
  };

  return (
    <>
      {open && <div style={{ position: "fixed", inset: 0, zIndex: 400 }} onClick={onClose} />}
      <div style={{
        position: "fixed", top: 0, right: 0, height: "100dvh", width: "min(360px, 100vw)",
        background: T.surface, borderLeft: `1px solid ${T.border}`,
        zIndex: 500, transform: open ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.25s ease", display: "flex", flexDirection: "column",
        boxShadow: open ? "-8px 0 32px rgba(0,0,0,0.08)" : "none",
      }}>
        {/* Header */}
        <div style={{ padding: "18px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <p style={{ fontSize: "14px", fontWeight: 700, color: T.text, fontFamily: F }}>Notifications</p>
            {unreadCount > 0 && <span style={{ background: CORAL, color: "#fff", borderRadius: "9999px", fontSize: "10px", fontWeight: 700, padding: "2px 7px", fontFamily: F }}>{unreadCount}</span>}
          </div>
          <div style={{ display: "flex", gap: "6px" }}>
            <Btn variant="ghost" size="xs" onClick={markAllRead}>Mark all read</Btn>
            <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: T.sub, padding: "4px", display: "flex" }}><X size={16} /></button>
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: "auto", scrollbarWidth: "none" }}>
          {notifications.length === 0 && (
            <p style={{ padding: "48px 20px", textAlign: "center", fontSize: "12px", color: T.muted, fontFamily: F }}>No notifications yet.</p>
          )}
          {notifications.map((n, i) => (
            <div key={n.id} style={{
              padding: "14px 18px", borderBottom: `1px solid ${T.border}`,
              background: n.read ? T.surface : "rgba(209,0,75,0.02)",
              cursor: "pointer", transition: "background 0.12s",
            }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = T.s2; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = n.read ? T.surface : "rgba(209,0,75,0.02)"; }}
              onClick={() => markRead(n.id)}
            >
              <div style={{ display: "flex", gap: "10px" }}>
                <div style={{ width: 28, height: 28, borderRadius: "7px", background: notifBg[n.type] ?? T.s2, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {notifIcons[n.type] ?? <Bell size={14} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "6px" }}>
                    <p style={{ fontSize: "12px", fontWeight: n.read ? 500 : 700, color: T.text, fontFamily: F, lineHeight: 1.4 }}>{n.title}</p>
                    {!n.read && <Dot color={CORAL} style={{ marginTop: "3px" }} />}
                  </div>
                  <p style={{ fontSize: "11px", color: T.sub, fontFamily: F, lineHeight: 1.55, marginTop: "3px" }}>{n.body}</p>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "8px" }}>
                    <p style={{ fontSize: "10px", color: T.muted, fontFamily: F }}>{n.timestamp}</p>
                    {n.actionLabel && n.actionTarget && (
                      <button onClick={(e) => { e.stopPropagation(); onNav(n.actionTarget!); onClose(); }}
                        style={{ fontSize: "11px", color: CORAL, fontFamily: F, fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: "3px" }}>
                        {n.actionLabel} <ChevronRight size={10} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
